/**
 * SOLID Review
 * S — Strong. One concern: what the client makes of the CMP's consent — the session's copy, whether
 *     advertising is allowed, whether this decision is one the server must hear of, and the audit record.
 * O — Good. A new category is a field in the schema; the signature and the rule read it.
 * L — N/A — functions.
 * I — Good. Two decisions on the one ClientDecisions contract.
 * D — Strong. The Domain's consent rule and policy version, the session draft and the port schemas.
 */
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession } from '../session/sessionDraft';
import { isAdvertisingAllowed } from '../../ports/IStateCookieConsent';
import { COOKIE_POLICY_VERSION } from '../../consent/cookiePolicyVersion';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type {
  ICommandConsentChanged, IEntityConsentRecord, ICommandCookieConsentRecord, IEntityCookieConsentRecord, IPayloadConsentState,
  IPayloadCookieConsent,
} from '../../ports/schemas/ConsentMoves';

/**
 * THE CMP'S DECISION, KEPT AND RECORDED (frontend strict-ports plan, FE4b, 2026-10-04). Moved from
 * CookieConsentProvider's subscription: every state the CMP reports becomes the session's consent;
 * advertising is allowed only once it is resolved with ad storage and ad personalisation granted; and a
 * RESOLVED state whose signature (source, region, the five categories) differs from the last one recorded
 * is recorded once. The signature is the session's now, so a re-subscribe that replays the same decision
 * records nothing.
 *
 * FIXED (§28.201): the record's policy version was the client's constant, `'1.0'`, since the server's
 * Cookie Policy went to 1.1 on 2026-06-26 (`3fabd2fe`) — every consent since was audited against a policy
 * version that was no longer in force. The version is the server's published Cookie Policy's now, and
 * the constant only a fallback when the documents cannot be read.
 */

type TDecided<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const COOKIE_POLICY = 'COOKIE_POLICY';

function signatureOf(state: IPayloadConsentState): string {
  const c = state.categories;
  return [state.source, state.region, c.adStorage, c.adUserData, c.adPersonalization, c.analyticsStorage, c.functionality].join('|');
}

/** The CMP reported a state: kept; and recorded if it is a resolved decision the server has not heard of. */
export function decideConsentChanged(input: IPropsClientInput, command: ICommandConsentChanged): TDecided<IEntityConsentRecord> {
  const { state } = command;
  const previous = input.session.consent;
  const signature = signatureOf(state);
  const record = state.resolved && signature !== previous.recordedSignature;
  const next = draftOf(input);
  next.session.consent = {
    resolved: state.resolved,
    categories: state.categories,
    region: state.region,
    source: state.source,
    advertisingAllowed: state.resolved && isAdvertisingAllowed(state.categories),
    // Marked before the call, as the provider did: a record that fails is not retried for the same decision.
    recordedSignature: record ? signature : previous.recordedSignature,
  };
  const dto: IEntityConsentRecord = { record };
  return decided(settleSession(input, next), dto);
}

/** The audit record: the decision, at the Cookie Policy version the server publishes. */
export function decidePlanCookieConsentRecord(_input: IPropsClientInput, command: ICommandCookieConsentRecord): TDecided<IEntityCookieConsentRecord> {
  const { state, published } = command;
  const policy = published.find((document) => document.type === COOKIE_POLICY);
  const request: IPayloadCookieConsent = {
    categories: state.categories,
    tcString: state.tcString,
    source: state.source,
    region: state.region,
    policyVersion: policy?.version ?? COOKIE_POLICY_VERSION,
  };
  const nothing: IPropsClientChanges = {};
  const dto: IEntityCookieConsentRecord = { request };
  return decided(nothing, dto);
}
